/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Wed Apr 24 2024
 **/

import { useRef, useState, useEffect } from "react";

import Tooltip from "./Tooltip";

type PropsWithoutChildren<P> = Required<DesignStuff.WithTooltip.Props<P>>["componentProps"];

export default function withTooltip<C extends React.ComponentType, P extends React.ComponentProps<C>>(
	Component: React.ComponentType<PropsWithoutChildren<P>>
) {
	const Wrapper = (props: DesignStuff.WithTooltip.Props<P>) => {
		const componentRef = useRef<HTMLElement>(null);
		const [toolTipMessageText, setToolTipMessageText] = useState<string>("");
		const [isEllipsis, setIsEllipsis] = useState<boolean>(false);
		const componentProps = props.componentProps || ({} as PropsWithoutChildren<P>);

		const tooltipProps = {
			message: toolTipMessageText,
			...props.tooltipProps
		};

		const handleMouseOver = (e: MouseEvent) => {
			const target = e.currentTarget as HTMLElement;
			const eleClientWidth = target.clientWidth;
			const eleScrollWidth = target.scrollWidth;

			setIsEllipsis(eleClientWidth < eleScrollWidth);
		};

		useEffect(() => {
			const textElement = componentRef.current;
			if (!textElement) return;
			textElement.addEventListener("mouseover", handleMouseOver);
			return () => {
				if (!textElement) return;
				textElement.removeEventListener("mouseover", handleMouseOver);
			};
		}, []);

		useEffect(() => {
			if (!isEllipsis) return;

			if (typeof props.children === "string") return setToolTipMessageText(props.children);

			if (isEllipsis && typeof props.children === "object") {
				const textArray = props.children as string[];
				return setToolTipMessageText(textArray.join().replaceAll(",", ""));
			}
		}, [isEllipsis]);

		return isEllipsis ? (
			<Tooltip {...tooltipProps} style={{ maxWidth: "323px", wordBreak: "break-all" }}>
				<Component ref={componentRef} {...componentProps}>
					{props.children}
				</Component>
			</Tooltip>
		) : (
			<Component ref={componentRef} {...componentProps}>
				{props.children}
			</Component>
		);
	};
	return Wrapper;
}
